import { useState, useMemo } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

interface NextBestActionProps {
  cases: Array<{
    id: string;
    studentName: string;
    estado: string;
    prioridad: string;
    judicializado?: boolean;
    diasSinAccion: number;
    tieneInsumosVencidos: boolean;
    tieneInformeVencido: boolean;
  }>;
  role: string;
  onActionClick?: (action: { type: string; caseId?: string; label: string }) => void;
  animate?: boolean;
}

export default function NextBestAction({ cases, role, onActionClick, animate = true }: NextBestActionProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector: ".nba-item",
    staggerDelay: 100,
    threshold: 0.1,
  });

  const actions = useMemo(() => {
    const result: Array<{ type: string; priority: "critical" | "high" | "medium"; label: string; description: string; icon: string; caseId?: string; color: "red" | "amber" | "primary" | "emerald" }> = [];

    // Check for overdue court reports
    const casesSinInforme = cases.filter((c) => c.tieneInformeVencido);
    if (casesSinInforme.length > 0) {
      result.push({
        type: "informe_vencido",
        priority: "critical",
        label: `${casesSinInforme.length} informe${casesSinInforme.length !== 1 ? "s" : ""} de tribunal vencido${casesSinInforme.length !== 1 ? "s" : ""}`,
        description: "Generar informe con IA y enviar a tribunal inmediatamente.",
        icon: "ri-file-warning-line",
        caseId: casesSinInforme[0].id,
        color: "red",
      });
    }

    // Check for overdue teacher inputs
    const casosSinInsumos = cases.filter((c) => c.tieneInsumosVencidos);
    if (casosSinInsumos.length > 0 && result.length < 3) {
      result.push({
        type: "insumos_vencidos",
        priority: "high",
        label: `${casosSinInsumos.length} docente${casosSinInsumos.length !== 1 ? "s" : ""} con insumos vencidos`,
        description: "Requerir entrega inmediata o reasignar solicitud.",
        icon: "ri-send-plane-line",
        caseId: casosSinInsumos[0].id,
        color: "red",
      });
    }

    // Check for stale cases (no action > 7 days)
    const casosEstancados = cases.filter((c) => c.diasSinAccion > 7);
    if (casosEstancados.length > 0 && result.length < 3) {
      result.push({
        type: "caso_estancado",
        priority: "high",
        label: `${casosEstancados.length} caso${casosEstancados.length !== 1 ? "s" : ""} sin intervención (&gt;7d)`,
        description: "Registrar intervención de seguimiento para reactivar el caso.",
        icon: "ri-time-line",
        caseId: casosEstancados[0].id,
        color: "amber",
      });
    }

    // Check for critical cases without assigned responsible
    const casosCriticosSinResponsable = cases.filter((c) => c.prioridad === "critica" && c.estado === "abierto");
    if (casosCriticosSinResponsable.length > 0 && result.length < 3) {
      result.push({
        type: "asignar_responsable",
        priority: "critical",
        label: "Caso crítico sin responsable asignado",
        description: "Asignar responsable para iniciar gestión inmediata.",
        icon: "ri-user-add-line",
        caseId: casosCriticosSinResponsable[0].id,
        color: "red",
      });
    }

    // Check for cases ready to close
    const casosParaCerrar = cases.filter((c) => c.estado === "en_seguimiento" && c.diasSinAccion <= 3);
    if (casosParaCerrar.length > 0 && result.length < 3) {
      result.push({
        type: "cerrar_caso",
        priority: "medium",
        label: `${casosParaCerrar.length} caso${casosParaCerrar.length !== 1 ? "s" : ""} listo${casosParaCerrar.length !== 1 ? "s" : ""} para cierre`,
        description: "Si se completaron todas las etapas, cerrar el caso.",
        icon: "ri-check-double-line",
        caseId: casosParaCerrar[0].id,
        color: "emerald",
      });
    }

    // Role-specific suggestions
    if (role === "orientador" && result.length < 3) {
      result.push({
        type: "solicitar_insumo",
        priority: "medium",
        label: "Solicitar insumo docente pendiente",
        description: "Hay casos judicializados que requieren antecedentes escolares.",
        icon: "ri-send-plane-line",
        color: "primary",
      });
    }
    if (role === "convivencia" && result.length < 3) {
      result.push({
        type: "registrar_medida",
        priority: "medium",
        label: "Registrar medida o acuerdo",
        description: "Casos activos requieren formalización de medidas adoptadas.",
        icon: "ri-hand-heart-line",
        color: "primary",
      });
    }
    if (role === "psicologo" && result.length < 3) {
      result.push({
        type: "registrar_seguimiento",
        priority: "medium",
        label: "Registrar seguimiento psicológico",
        description: "Estudiantes en intervención requieren registro de evolución.",
        icon: "ri-user-heart-line",
        color: "primary",
      });
    }
    if ((role === "director" || role === "sostenedor") && result.length < 3) {
      result.push({
        type: "descargar_reporte",
        priority: "medium",
        label: "Descargar reporte consolidado",
        description: "Revisar indicadores institucionales del período actual.",
        icon: "ri-file-chart-line",
        color: "primary",
      });
    }
    if (role === "docente" && result.length < 3) {
      const pendientes = cases.filter((c) => c.estado === "pendiente");
      if (pendientes.length > 0) {
        result.push({
          type: "completar_formulario",
          priority: "high",
          label: `${pendientes.length} solicitud${pendientes.length !== 1 ? "es" : ""} pendiente${pendientes.length !== 1 ? "s" : ""} por responder`,
          description: "Completar formulario guiado para enviar antecedentes.",
          icon: "ri-file-text-line",
          color: "amber",
        });
      }
    }

    return result.slice(0, 3);
  }, [cases, role]);

  if (actions.length === 0) {
    return (
      <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-2xl p-5">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-100">
            <i className="ri-check-double-line text-emerald-500 text-lg" />
          </span>
          <div>
            <p className="text-sm font-bold text-emerald-800">Todo al día</p>
            <p className="text-xs text-emerald-600">No hay acciones críticas pendientes. ¡Buen trabajo!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">Próxima acción recomendada</p>
      <div ref={animate ? ref : undefined}>
        {actions.map((action, idx) => {
          const colors: Record<string, { bg: string; border: string; text: string; iconBg: string; hover: string }> = {
            red: { bg: "bg-red-50/60", border: "border-red-200/60", text: "text-red-700", iconBg: "bg-red-100", hover: "hover:bg-red-50" },
            amber: { bg: "bg-amber-50/60", border: "border-amber-200/60", text: "text-amber-700", iconBg: "bg-amber-100", hover: "hover:bg-amber-50" },
            primary: { bg: "bg-primary-50/60", border: "border-primary-200/60", text: "text-primary-700", iconBg: "bg-primary-100", hover: "hover:bg-primary-50" },
            emerald: { bg: "bg-emerald-50/60", border: "border-emerald-200/60", text: "text-emerald-700", iconBg: "bg-emerald-100", hover: "hover:bg-emerald-50" },
          };
          const c = colors[action.color] || colors.primary;
          const isFirst = action.priority === "critical";

          return (
            <div
              key={idx}
              onClick={() => onActionClick?.({ type: action.type, caseId: action.caseId, label: action.label })}
              className={`nba-item ${animate ? "scroll-reveal" : ""} flex items-start gap-3 p-4 rounded-xl border ${c.bg} ${c.border} ${c.hover} transition-all duration-200 cursor-pointer group ${isFirst ? "shadow-glow-red animate-pulse-glow" : "hover-lift"}`}
              style={animate ? { transitionDelay: `${idx * 100}ms` } : undefined}
            >
              <span className={`flex items-center justify-center w-9 h-9 rounded-lg ${c.iconBg} ${c.text} flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform`}>
                <i className={`${action.icon} text-base`} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-bold ${c.text} truncate`}>{action.label}</p>
                  {isFirst && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold flex-shrink-0">AHORA</span>
                  )}
                </div>
                <p className="text-xs text-foreground-500 mt-0.5">{action.description}</p>
              </div>
              <span className="flex items-center justify-center w-4 h-4 text-foreground-400 group-hover:translate-x-1 transition-transform flex-shrink-0 mt-2">
                <i className="ri-arrow-right-s-line" />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}